import React, { useEffect, useState } from "react";
import { attendanceApi } from "@api/attendance/attendance";
import {
  Loader,
  Message,
  Statistic,
  Grid,
  Segment,
  Tab,
  Header,
  Container,
  Divider,
} from "semantic-ui-react";
import AttendanceCharts from "./components/AttendanceCharts";
import UserSessionTable from "./components/UserSessionTable";

const AttendanceSummary = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allSessions, setAllSessions] = useState([]);
  const [dailyCounts, setDailyCounts] = useState({});
  const [weeklyCounts, setWeeklyCounts] = useState({});
  const [monthlyCounts, setMonthlyCounts] = useState({});
  const [rawSessions, setRawSessions] = useState([]);
  const [hourlyPeakData, setHourlyPeakData] = useState({
    peakHour: null,
    peakCount: 0,
    hourlyStats: {},
  });

  useEffect(() => {
    const fetchDashboardSummary = async () => {
      try {
        setIsLoading(true);

        const dashboardResponse = await attendanceApi.getDashboardSummary();
        const allSessionsResponse = await attendanceApi.list("");

        if (dashboardResponse.status === "success" && dashboardResponse.data) {
          const { activeSessions = [] } = dashboardResponse.data;
          setRawSessions(activeSessions);

          let allSessionsData = [];
          if (allSessionsResponse.data && allSessionsResponse.data.hits) {
            allSessionsData = allSessionsResponse.data.hits;
          }

          const combinedSessions = [...activeSessions];
          allSessionsData.forEach((session) => {
            if (!combinedSessions.find((s) => s.id === session.id)) {
              combinedSessions.push(session);
            }
          });

          setAllSessions(combinedSessions);

          const completedSessions = combinedSessions.filter(
            (session) => session.outTime || session.out_time
          );
          const { daily, weekly, monthly } = aggregateByDate(completedSessions);
          setDailyCounts(daily);
          setWeeklyCounts(weekly);
          setMonthlyCounts(monthly);

          const hourlyData = calculateHourlyPeakData(combinedSessions);
          setHourlyPeakData(hourlyData);
        } else {
          throw new Error(
            dashboardResponse.message || "Invalid response structure"
          );
        }
      } catch (err) {
        setError(err.message || "Unexpected error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardSummary();
  }, []);

  // ----------------------------------------
  // Helper arrays for labels
  // ----------------------------------------
  const weekdayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const monthLabels = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Aggregate dailyCounts into weekdays Mon - Sun
  const aggregateByWeekday = (dailyCounts) => {
    const weekdayCounts = {
      Mon: 0,
      Tue: 0,
      Wed: 0,
      Thu: 0,
      Fri: 0,
      Sat: 0,
      Sun: 0,
    };
    Object.entries(dailyCounts).forEach(([dateStr, count]) => {
      const date = new Date(dateStr);
      if (isNaN(date)) return;

      const day = date.getDay(); // 0=Sunday, 1=Monday...
      const weekdayIndex = day === 0 ? 6 : day - 1; // Shift Sunday to the end
      const weekdayLabel = weekdayLabels[weekdayIndex];
      weekdayCounts[weekdayLabel] += count;
    });
    return weekdayCounts;
  };

  // Map monthlyCounts keys (YYYY-MM) to month names, aggregated and ordered
  const mapMonthlyCountsToLabels = (monthlyCounts) => {
    const countsByMonth = monthLabels.reduce((acc, month) => {
      acc[month] = 0;
      return acc;
    }, {});

    Object.entries(monthlyCounts).forEach(([key, count]) => {
      // key format: YYYY-MM
      const parts = key.split("-");
      if (parts.length !== 2) return;
      const monthIndex = parseInt(parts[1], 10) - 1;
      const monthName = monthLabels[monthIndex];
      if (monthName) countsByMonth[monthName] += count;
    });

    return countsByMonth;
  };

  // Build hourly chart data from hourlyStats (hourlyPeakData.hourlyStats)
  const buildHourlyChartData = (hourlyStats) => {
    const labels = Array.from(
      { length: 24 },
      (_, i) => i.toString().padStart(2, "0") + ":00"
    );
    const data = labels.map((label) => hourlyStats[label] || 0);

    return {
      labels,
      datasets: [
        {
          label: "Hourly Sessions",
          data,
          backgroundColor: "rgba(153, 102, 255, 0.6)",
          borderColor: "rgba(153, 102, 255, 1)",
          borderWidth: 1,
        },
      ],
    };
  };

  // Existing aggregateByDate unchanged
  const aggregateByDate = (sessions) => {
    const daily = {};
    const weekly = {};
    const monthly = {};

    sessions.forEach((session) => {
      const dateField = session.outTime || session.out_time;
      if (!dateField) return;

      const date = new Date(dateField);
      if (isNaN(date)) return;

      const dayKey = date.toISOString().split("T")[0];
      const weekKey = `${date.getFullYear()}-W${getWeekNumber(date)}`;
      const monthKey = `${date.getFullYear()}-${(date.getMonth() + 1)
        .toString()
        .padStart(2, "0")}`;

      daily[dayKey] = (daily[dayKey] || 0) + 1;
      weekly[weekKey] = (weekly[weekKey] || 0) + 1;
      monthly[monthKey] = (monthly[monthKey] || 0) + 1;
    });

    return { daily, weekly, monthly };
  };

  const getWeekNumber = (d) => {
    d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  };

  const calculateHourlyPeakData = (sessions) => {
    const hourlyStats = {};
    let peakHour = null;
    let peakCount = 0;

    sessions.forEach((session) => {
      const inTimeField =
        session.inTime || session.in_time || session.inTimeDisplay;
      if (inTimeField) {
        const inDate = new Date(inTimeField);
        if (!isNaN(inDate)) {
          const hour = inDate.getHours();
          const hourKey = `${hour.toString().padStart(2, "0")}:00`;
          hourlyStats[hourKey] = (hourlyStats[hourKey] || 0) + 1;
        }
      }

      const outTimeField = session.outTime || session.out_time;
      if (outTimeField) {
        const outDate = new Date(outTimeField);
        if (!isNaN(outDate)) {
          const hour = outDate.getHours();
          const hourKey = `${hour.toString().padStart(2, "0")}:00`;
          hourlyStats[hourKey] = (hourlyStats[hourKey] || 0) + 1;
        }
      }
    });

    Object.entries(hourlyStats).forEach(([hour, count]) => {
      if (count > peakCount) {
        peakCount = count;
        peakHour = hour;
      }
    });

    return {
      peakHour: peakHour || "N/A",
      peakCount,
      hourlyStats,
    };
  };

  const formatPeakHourDisplay = () => {
    if (!hourlyPeakData.peakHour || hourlyPeakData.peakHour === "N/A") {
      return "N/A";
    }

    const hour = parseInt(hourlyPeakData.peakHour.split(":")[0], 10);
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;

    return `${displayHour}:00 ${period}`;
  };

  // Modify buildChartData to accept ordered objects (weekday or month names)
  const buildChartData = (counts, label) => {
    const keys = Object.keys(counts);
    // Sort keys by weekday or month order when applicable (else default sort)
    let sortedKeys = [...keys];

    if (keys.length === 7 && keys.every((k) => weekdayLabels.includes(k))) {
      // It's weekday data - sort by weekdayLabels order
      sortedKeys.sort(
        (a, b) => weekdayLabels.indexOf(a) - weekdayLabels.indexOf(b)
      );
    } else if (
      keys.length === 12 &&
      keys.every((k) => monthLabels.includes(k))
    ) {
      // Month data
      sortedKeys.sort(
        (a, b) => monthLabels.indexOf(a) - monthLabels.indexOf(b)
      );
    } else {
      // Default alpha
      sortedKeys.sort();
    }

    return {
      labels: sortedKeys,
      datasets: [
        {
          label,
          data: sortedKeys.map((key) => counts[key] || 0),
          backgroundColor: "rgba(54, 162, 235, 0.6)",
          borderColor: "rgba(54, 162, 235, 1)",
          borderWidth: 1,
        },
      ],
    };
  };

  // Comparison chart data remains unchanged
  const buildComparisonChartData = () => {
    const allDates = new Set([
      ...Object.keys(dailyCounts),
      ...Object.keys(weeklyCounts),
      ...Object.keys(monthlyCounts),
    ]);
    const sortedDates = Array.from(allDates).sort();

    return {
      labels: sortedDates,
      datasets: [
        {
          label: "Daily",
          data: sortedDates.map((date) => dailyCounts[date] || 0),
          borderColor: "rgba(255, 99, 132, 1)",
          backgroundColor: "rgba(255, 99, 132, 0.2)",
          tension: 0.4,
        },
        {
          label: "Weekly",
          data: sortedDates.map((date) => weeklyCounts[date] || 0),
          borderColor: "rgba(54, 162, 235, 1)",
          backgroundColor: "rgba(54, 162, 235, 0.2)",
          tension: 0.4,
        },
        {
          label: "Monthly",
          data: sortedDates.map((date) => monthlyCounts[date] || 0),
          borderColor: "rgba(75, 192, 192, 1)",
          backgroundColor: "rgba(75, 192, 192, 0.2)",
          tension: 0.4,
        },
      ],
    };
  };

  const calculateStatistics = () => {
    if (!allSessions.length) {
      return {
        totalSessions: 0,
        activeSessions: 0,
        completedSessions: 0,
        uniqueUsers: 0,
      };
    }

    const activeSessions = allSessions.filter(
      (session) => !session.outTime && !session.out_time
    );
    const completedSessions = allSessions.filter(
      (session) => session.outTime || session.out_time
    );
    const uniqueUserIds = new Set(
      allSessions.map((session) => session.libraryId || session.library_id)
    ).size;

    return {
      totalSessions: allSessions.length,
      activeSessions: activeSessions.length,
      completedSessions: completedSessions.length,
      uniqueUsers: uniqueUserIds,
    };
  };

  const stats = calculateStatistics();

  const renderUserSessions = () => (
    <Tab.Pane attached={false}>
      <UserSessionTable
        allSessions={allSessions}
        isLoading={isLoading}
        rawSessions={rawSessions}
        hourlyPeakData={hourlyPeakData}
      />
    </Tab.Pane>
  );

  const renderAttendanceCharts = () => (
    <Tab.Pane attached={false}>
      <AttendanceCharts
        dailyCounts={aggregateByWeekday(dailyCounts)}
        weeklyCounts={weeklyCounts}
        monthlyCounts={mapMonthlyCountsToLabels(monthlyCounts)}
        hourlyStats={hourlyPeakData.hourlyStats}
        buildChartData={buildChartData}
        buildHourlyChartData={buildHourlyChartData}
        buildComparisonChartData={buildComparisonChartData}
        isLoading={isLoading}
      />
    </Tab.Pane>
  );

  const panes = [
    {
      menuItem: { key: "users", icon: "users", content: "All Sessions" },
      render: renderUserSessions,
    },
    {
      menuItem: {
        key: "charts",
        icon: "chart bar",
        content: "Attendance Charts",
      },
      render: renderAttendanceCharts,
    },
  ];

  return (
    <Container fluid style={{ padding: "20px", backgroundColor: "#f8f9fa" }}>
      <Segment raised style={{ marginBottom: "20px" }}>
        <Header as="h1" textAlign="center" style={{ marginBottom: "15px" }}>
          <Header.Content>
            Library Attendance Dashboard
            <Header.Subheader
              style={{ marginTop: "5px", fontSize: "0.50em", color: "#6c757d" }}
            >
              Complete session history with real-time monitoring and analytics
            </Header.Subheader>
          </Header.Content>
        </Header>

        <Divider />

        {!isLoading && !error && (
          <Grid
            columns={5}
            divided
            stackable
            textAlign="center"
            style={{ margin: 0 }}
          >
            <Grid.Row style={{ paddingTop: "15px", paddingBottom: "15px" }}>
              <Grid.Column>
                <Statistic size="mini" color="green">
                  <Statistic.Value>{stats.totalSessions}</Statistic.Value>
                  <Statistic.Label>Total Sessions</Statistic.Label>
                </Statistic>
              </Grid.Column>
              <Grid.Column>
                <Statistic size="mini" color="blue">
                  <Statistic.Value>{stats.uniqueUsers}</Statistic.Value>
                  <Statistic.Label>Unique Users</Statistic.Label>
                </Statistic>
              </Grid.Column>
              <Grid.Column>
                <Statistic size="mini" color="teal">
                  <Statistic.Value>{stats.activeSessions}</Statistic.Value>
                  <Statistic.Label>Currently Active</Statistic.Label>
                </Statistic>
              </Grid.Column>
              <Grid.Column>
                <Statistic size="mini" color="orange">
                  <Statistic.Value>{stats.completedSessions}</Statistic.Value>
                  <Statistic.Label>Completed Sessions</Statistic.Label>
                </Statistic>
              </Grid.Column>
              <Grid.Column>
                <Statistic size="mini" color="purple">
                  <Statistic.Value>{formatPeakHourDisplay()}</Statistic.Value>
                  <Statistic.Label>Peak Hour</Statistic.Label>
                </Statistic>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        )}
      </Segment>

      {isLoading && (
        <Segment placeholder style={{ minHeight: "200px" }}>
          <Loader active inline="centered" size="large">
            Loading attendance data...
          </Loader>
        </Segment>
      )}

      {error && (
        <Message negative size="large" icon="warning circle">
          <Message.Content>
            <Message.Header>Error Loading Data</Message.Header>
            <p>{error}</p>
          </Message.Content>
        </Message>
      )}

      {!isLoading && !error && (
        <Tab
          menu={{ secondary: true, pointing: true }}
          panes={panes}
          style={{ marginTop: "0" }}
        />
      )}
    </Container>
  );
};

export default AttendanceSummary;
export { AttendanceSummary };
